import _ = require('underscore');
import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { Set } from '../../../ts/models/Set';
import { SetOfUsers } from '../../../ts/models/SetOfUsers';
import { ServerUsers } from '../../../ts/resources/ServerUsers';
import { IUpdateServerUsersSiteMembershipResult } from '../../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IUserActionResult, UserActionResultCode } from '../../../ts/ServerApi/types/IUserActionResult';
import { ServerService } from '../../../ts/services/ServerService';
import { AuthenticationProviderType, IAuthenticationProvider } from '../../../ts/services/SiteAuthenticationSettingsService';
import { SiteRolesPickerClientService } from '../../../ts/services/SiteRolesPickerClientService';
import { SiteMembershipUpdateActionNotification, UserActionNotification } from '../../../ts/services/UserActionNotification';
import { ServiceLocator } from '../../ServiceLocator';
import { AddLocalUsersDialogProps, INewUserFlowModel } from '../AddLocalUsersDialog';
import { ISiteRoleChanges } from '../SiteRolesPickerContainer';

export class CreateLocalServerUserHelperService {
  private server: ServerService = ServiceLocator.get<ServerService>('ServerService');
  private serverUsers: ServerUsers = ServiceLocator.get<ServerUsers>('ServerUsers');
  private userActionNotification: UserActionNotification = ServiceLocator.get<UserActionNotification>('UserActionNotification');
  private rolesPickerClient: SiteRolesPickerClientService = ServiceLocator.get<SiteRolesPickerClientService>('SiteRolesPickerClientService');
  private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification = ServiceLocator.get<SiteMembershipUpdateActionNotification>('SiteMembershipUpdateActionNotification');

  public createLocalServerUser(username: string, model: INewUserFlowModel, authenticationProvider: IAuthenticationProvider,
                            siteRoleChanges: ISiteRoleChanges, props: AddLocalUsersDialogProps, setBusyState: (busy: boolean) => void) {
    let action: ng.IPromise<IUserActionResult>;
    const isLocalAuth = authenticationProvider.type === AuthenticationProviderType.default && !this.server.isAuthenticationSaml();
    if (isLocalAuth) {
      action = this.serverUsers.createLocalServerUser(
      username,
      model.displayName,
      model.email,
      model.password);
    } else {
      action = this.serverUsers.createLocalServerUserWithoutPassword(
        username,
        model.displayName,
        model.email
      );
    }
    action.then((result: IUserActionResult) => {
      if (!!result.errors && result.errors.length > 0) {
        setBusyState(false);
      } else {
        this.onServerUserCreateSuccess(result, siteRoleChanges, props);
      }
    });
  }

  public onServerUserCreateSuccess(result: IUserActionResult, siteRoleChanges: ISiteRoleChanges, props: AddLocalUsersDialogProps) {
    // create an interfaced object for the created user
    const addedUser: IUserInfo = {
      id: result.users[0].id,
      authId: result.users[0].authId,
      username: result.users[0].username,
      displayName: result.users[0].displayName
    };

    // update site membership
    if (!!siteRoleChanges.serverAdmin) {
      this.updateServerAdmin(addedUser, siteRoleChanges, props);
    } else if (!_.isEmpty(siteRoleChanges.siteRoles)) {
      this.updateSiteMembership(addedUser.id, addedUser.authId, addedUser.username, addedUser.displayName, siteRoleChanges, props);
    } else {
      this.showNotificationForCreateServerUser(addedUser, result, props);
    }
  }

  public showNotificationForCreateServerUser(user: IUserInfo, result: IUserActionResult, props: AddLocalUsersDialogProps){
    this.userActionNotification.notify({
      users: Set.fromIdItemArray(result.users),
      results: result.results,
      errors: result.errors,
      successCode: UserActionResultCode.systemUserCreated
    });
    props.emitSiteRolesChanged();
    props.close(user);
  }

  // section: update server admin

  public updateServerAdmin(user: IUserInfo, siteRoleChanges: ISiteRoleChanges, props: AddLocalUsersDialogProps) {
    const users = SetOfUsers.singletonForUser(user);
    const newAdminState = (siteRoleChanges.serverAdmin === CheckboxState.checked);
    this.rolesPickerClient.updateServerAdmin(users, newAdminState).then(() => {
      props.emitSiteRolesChanged();
      props.close(user);
    });
  }

  // section: update site membership or roles

  public updateSiteMembership(userId: string, authUserId: string, username: string, userDisplayName: string, siteRoleChanges: ISiteRoleChanges, props: AddLocalUsersDialogProps) {
    const params = this.rolesPickerClient.siteRoleChangesToParams([userId], siteRoleChanges.siteRoles);
    this.rolesPickerClient.updateServerUsersSiteMembership(params)
      .then((result: IUpdateServerUsersSiteMembershipResult) => {
        // create a Set<T> object for the result notification system
        const addedUser: IUserInfo = {
          id: userId,
          authId: authUserId,
          username: username,
          displayName: userDisplayName
        };
        const users = SetOfUsers.singletonForUser(addedUser);

        if(result.results){
          this.siteMembershipUpdateActionNotification.notify(
              result.results,
              users,
              siteRoleChanges.siteRoles);
        }
        props.emitSiteRolesChanged();
        props.close(addedUser);
      });
  }
}
